<template>
  <div class="summary-block">
    <section class="summary-strip" aria-label="배달 매출 요약">
      <button
        v-for="item in items"
        :key="item.key"
        type="button"
        class="summary-tile"
        :data-testid="`summary-${item.key}`"
        :class="{ clickable: item.clickable }"
        :aria-label="item.label"
        @click="handleClick(item)">
        <span class="tile-label">
          <i :class="item.icon"></i>
          {{ item.label }}
        </span>
        <strong>{{ formatCurrency(item.value) }}</strong>
        <span class="tile-meta">
          <span v-if="item.percent" class="meta-percent" :data-testid="`summary-${item.key}-percent`"
            >{{ item.percent }} ·
          </span>
          {{ item.meta }}
        </span>
      </button>
    </section>
    <p v-if="estimatedSales > 0" class="estimate-note" data-testid="estimate-note">
      <i class="el-icon-info"></i>
      {{ $t('수수료 정보가 아직 없는 매출') }}
      <strong>{{ formatCurrency(estimatedSales) }}</strong
      >{{ $t('은(는) 38%로 추정 적용했습니다') }}
    </p>
  </div>
</template>

<script>
import { DEDUCTION_KEYS } from '@/utils/integratedSalesDeductions'

export default {
  props: {
    summary: {
      type: Object,
      default: () => ({}),
    },
    savings: {
      type: Object,
      default: () => ({}),
    },
    // 차감 구성요소 맵(서버 signed) — 가게 수취(음수 컴포넌트)를 분리 표기하는 데 쓴다.
    deductions: {
      type: Object,
      default: () => ({}),
    },
  },
  computed: {
    estimatedSales() {
      return Number(this.summary.estimated_sales) || 0
    },
    // 가게 수취 합계(요기요 배달요금·배민 가게배달팁 등 음수 컴포넌트의 절대값 합).
    // 수수료 타일 값은 순차감(수취 상쇄 후)이라, 수취가 있으면 구성(차감 − 수취)을 부제로 밝힌다.
    creditTotal() {
      return DEDUCTION_KEYS.reduce((sum, key) => {
        const value = Number(this.deductions[key] || 0)
        return value < 0 ? sum + Math.abs(value) : sum
      }, 0)
    },
    netFees() {
      return (Number(this.summary.deduction_amount) || 0) + (Number(this.summary.estimated_fee) || 0)
    },
    // 수수료 예상(실수집 차감 + 추정)이 기준매출(gross)의 몇 %인지.
    // 기준매출 0 이하 또는 순차감이 음수(수취가 차감보다 큼)면 비율 표시 생략.
    feeRatePercent() {
      const sales = Number(this.summary.order_amount) || 0
      if (sales <= 0 || this.netFees < 0) return null
      return ((this.netFees / sales) * 100).toFixed(1)
    },
    items() {
      return [
        {
          key: 'sales',
          // 정산 기준매출(gross) — 통계 화면의 '매출'(실결제, net)과 기준이 다름 (SalesBasisInfo 안내 참조)
          label: this.$t('기준매출(정산)'),
          value: this.summary.order_amount || 0,
          meta: `${this.summary.transaction_count || 0}${this.$t('건')}`,
          icon: 'el-icon-s-data',
        },
        {
          key: 'fees',
          label: this.$t('수수료 예상'),
          value: this.netFees,
          percent: this.feeRatePercent === null ? null : `${this.$t('기준매출의')} ${this.feeRatePercent}%`,
          // 가게 수취(팁·요기요 배달요금)가 있으면 순차감 구성을 밝힌다 — '수수료 31.2%' 같은
          // 오독(가게부담 할인·수취 미구분) 방지.
          meta:
            this.creditTotal > 0
              ? `${this.$t('차감')} ${this.formatCurrency(this.netFees + this.creditTotal)} − ${this.$t(
                  '가게 수취'
                )} ${this.formatCurrency(this.creditTotal)}`
              : Number(this.summary.estimated_fee) > 0
              ? this.$t('실수집 + 미수집 38% 추정')
              : this.$t('차감 내역 합계'),
          icon: 'el-icon-bank-card',
        },
        {
          key: 'expected_settlement',
          label: this.$t('정산 예상'),
          value: this.summary.expected_settlement || 0,
          meta: this.$t('기준매출 - 수수료 예상'),
          icon: 'el-icon-wallet',
        },
        {
          key: 'savings',
          label: this.$t('절감액'),
          value: this.savings.estimatedOrdertalkSavings || 0,
          meta: this.$t('오더톡 전환 가정'),
          icon: 'el-icon-data-line',
          clickable: true,
        },
      ]
    },
  },
  methods: {
    formatCurrency(value) {
      return `${Number(value || 0).toLocaleString()}원`
    },
    handleClick(item) {
      if (item.clickable) {
        this.$emit('open-savings')
      }
    },
  },
}
</script>

<style scoped>
.summary-strip {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 12px;
}

.estimate-note {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 5px;
  margin: 10px 0 0;
  color: var(--color-text-secondary);
  font-size: 12px;
  line-height: 1.4;
}

.estimate-note i {
  color: var(--color-primary);
}

.estimate-note strong {
  display: inline;
  margin-top: 0;
  color: var(--color-text-primary);
  font-size: 12px;
  font-weight: 700;
}

.summary-tile {
  min-width: 0;
  min-height: 118px;
  padding: 16px;
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
  background: var(--color-bg-card);
  box-shadow: var(--shadow-card);
  color: var(--color-text-primary);
  text-align: left;
}

.summary-tile.clickable {
  cursor: pointer;
}

.summary-tile.clickable:hover,
.summary-tile:focus-visible {
  border-color: var(--color-primary);
  outline: 2px solid rgba(var(--color-primary-rgb), 0.18);
  outline-offset: 1px;
}

.tile-label {
  display: flex;
  align-items: center;
  gap: 6px;
  color: var(--color-text-regular);
  font-size: 13px;
  font-weight: 600;
}

.tile-label i {
  color: var(--color-primary);
  font-size: 16px;
}

strong {
  display: block;
  margin-top: 12px;
  color: var(--color-text-primary);
  font-size: 24px;
  line-height: 1.2;
  word-break: keep-all;
}

.tile-meta {
  display: block;
  margin-top: 8px;
  color: var(--color-text-secondary);
  font-size: 12px;
}

.meta-percent {
  color: var(--color-primary-dark);
  font-weight: 700;
}

@media (max-width: 1024px) {
  .summary-strip {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

@media (max-width: 480px) {
  .summary-strip {
    grid-template-columns: 1fr;
  }

  .summary-tile {
    min-height: 96px;
  }

  strong {
    font-size: 20px;
  }
}
</style>
